import {
  State,
  Action,
  StateContext,
  Selector,
} from '@ngxs/store';
import { EngineNodesStateModel, ObjectId } from './engine.nodes.state.model';
import { NodeActions } from './engine.nodes.actions';

import { UndoActions } from 'src/app/undo/state/undo.state.actions';
import { setAllChildren } from './node-utils';
import { calcShownObjectState } from './node-reducers';
import { Injectable } from '@angular/core';
import { ColorRecipeModel } from 'src/app/edit/models/color-recipe.model';

declare const immer;

const initialState: EngineNodesStateModel = {
  activeChapterId: undefined,
  nodeState: undefined,
  patches: [],
  patchesForChapter: {}
};

@State<EngineNodesStateModel>({
  name: 'engineNodes',
  defaults: initialState
})
@Injectable()
export class EngineNodesState {
  /**
   * Selectors
   */
  @Selector()
  static getAllNodeState(state: EngineNodesStateModel) {
    return state.nodeState;
  }

  @Selector()
  static getActiveChapter(state: EngineNodesStateModel) {
    return state.activeChapterId;
  }

  @Selector()
  static getPatches(state: EngineNodesStateModel) {
    return state.patches;
  }

  @Selector()
  static getNodes(state: EngineNodesStateModel) {
    if (state.activeChapterId === undefined || state.nodeState === undefined) {
      return {};
    }
    return state.nodeState[state.activeChapterId] || {};
  }

  @Selector()
  static getNodePaint(state: EngineNodesStateModel) {
    return (objectId: ObjectId) =>
      state.nodeState[state.activeChapterId][objectId].paint;
  }

  @Selector()
  static getNodeIds(state: EngineNodesStateModel) {
    return Object.keys(EngineNodesState.getNodes(state));
  }

  @Selector()
  static getCheckedNodeIds(state: EngineNodesStateModel) {
    return Object.values(EngineNodesState.getNodes(state))
      .filter(nodeState => {
        return nodeState.checked;
      })
      .map(nodeState => nodeState.objectId);
  }

  @Selector()
  static getCheckedNodes(state: EngineNodesStateModel) {
    return EngineNodesState.getCheckedNodeIds(state).map((objectId) => {
      return state.nodeState[state.activeChapterId][objectId];
    });
  }

  @Selector()
  static getUncheckedNodeIds(state: EngineNodesStateModel) {
    return EngineNodesState.getNodeIds(state).filter(objectId => {
      const node = state.nodeState[state.activeChapterId][objectId];
      return !node.checked;
    });
  }

  @Selector()
  static getSelectedNode(state: EngineNodesStateModel) {
    const selectedNodeId = EngineNodesState.getNodeIds(state).find(objectId => {
    
      return state.nodeState[state.activeChapterId][objectId].selected;
    });

    return selectedNodeId
      ? state.nodeState[state.activeChapterId][selectedNodeId]
      : undefined;
  }

  /**
   * Actions
   */

  @Action(NodeActions.Reset)
  reset(context: StateContext<EngineNodesStateModel>, action: NodeActions.Reset) {
    context.patchState(
      immer.produce(context.getState(), draft => {
        draft.activeChapterId = undefined;
        draft.nodeState = undefined;
        draft.patches = [];
        draft.patchesForChapter = {};
      })
    );
  }

  @Action(NodeActions.ApplyPatch)
  applyPatch(
    context: StateContext<EngineNodesStateModel>,
    action: NodeActions.ApplyPatch
  ) {
    const nextState = immer.applyPatches(context.getState(), action.patches);

    // need to preserve patches when we undo!
    const finalState = immer.produce(nextState, draft => {
      draft.patchesForChapter[draft.activeChapterId].push(...action.patches);
      draft.patches = action.patches;
    });

    context.patchState(finalState);
  }

  @Action(NodeActions.InitNodeState)
  initNodeState(
    context: StateContext<EngineNodesStateModel>,
    action: NodeActions.InitNodeState
  ) {
    context.patchState({ nodeState: action.nodeState });
  }

  @Action(NodeActions.SetActiveChapter)
  setActiveChapter(
    context: StateContext<EngineNodesStateModel>,
    action: NodeActions.SetActiveChapter
  ) {
    const state = context.getState();

    const nextState = immer.produce(state, draft => {
      draft.activeChapterId = action.activeChapterId;
      draft.patchesForChapter[action.activeChapterId] =
        draft.patchesForChapter[action.activeChapterId] || [];
      draft.patches = draft.patchesForChapter[action.activeChapterId];
    });

    context.patchState(nextState);
  }

  @Action(NodeActions.DuplicateNodeState)
  duplicateNodeState(
    context: StateContext<EngineNodesStateModel>,
    action: NodeActions.DuplicateNodeState
  ) {
    const state = context.getState();

    const nextState = immer.produce(state, draft => {
      draft.nodeState[action.copyChapterId] =
        draft.nodeState[action.originalChapterId];

      // when I copy patches over to a new chapter, I must replace the chapter Id for each patch.
      const patches = draft.patchesForChapter[action.originalChapterId];

      draft.patchesForChapter[action.copyChapterId] = patches.map(patch => {
        patch.path[1] = action.copyChapterId;
        return patch;
      });
    });

    context.patchState(nextState);
  }

  @Action(NodeActions.UpdateNodesShown)
  updateNodesShown(
    context: StateContext<EngineNodesStateModel>,
    action: NodeActions.UpdateNodesShown
  ) {

    const state = context.getState();
    const {finalState, patches, inversePatches} = calcShownObjectState(state, action);
    context.patchState(finalState);
    if (action.record) {
      context.dispatch(
        new UndoActions.SavePatches([{
          forward: patches,
          inverse: inversePatches,
          patchAction: NodeActions.ApplyPatch
        }])
      );
    }
  }


  @Action(NodeActions.UpdateNodeSelected)
  updateNodesSelected(
    context: StateContext<EngineNodesStateModel>,
    action: NodeActions.UpdateNodeSelected
  ) {
    const state = context.getState();
    const nextState = immer.produce(state, draft => {
      const nodes = draft.nodeState[draft.activeChapterId];
      const objectIds = Object.keys(nodes);
      switch (action.objectId) {
        case undefined: {
          objectIds.forEach(objectId => (nodes[objectId].selected = false));
          break;
        }
        default: {
          objectIds.forEach(objectId => (nodes[objectId].selected = false));
          nodes[action.objectId].selected = true;
          break;
        }
      }
    });

    context.patchState(nextState);
  }

  @Action(NodeActions.UpdateNodesChecked)
  updateNodesChecked(
    context: StateContext<EngineNodesStateModel>,
    action: NodeActions.UpdateNodesChecked
  ) {
    const state = context.getState();
    const [nextState, patches, inversePatches] = immer.produceWithPatches(
      state,
      draft => {
        const nodes = draft.nodeState[draft.activeChapterId];
        switch (action.patch) {
          case undefined: {
            const objectIds = action.objectIds || Object.keys(nodes);
            objectIds.forEach(
              objectId => {
                nodes[objectId].checked = action.checked;
                if (action.objectIds) {
                  setAllChildren(objectId, nodes, 'checked', action.checked);
                }
              }
            );
            break;
          }
          default: {
            Object.entries(action.patch).forEach(
              ([objectId, value]) => (nodes[objectId].checked = value)
            );
            break;
          }
        }
      }
    );


    const finalState = immer.produce(nextState, draft => {
      draft.patches = patches;
    });

    context.patchState(finalState);
  }

  @Action(NodeActions.UpdateNodesPickable)
  updateNodesPickable(
    context: StateContext<EngineNodesStateModel>,
    action: NodeActions.UpdateNodesPickable
  ) {
    const state = context.getState();
    const [nextState, patches, inversePatches] = immer.produceWithPatches(
      state,
      draft => {
        const nodes = draft.nodeState[draft.activeChapterId];
        if (action.patch) {
          Object.entries(action.patch).forEach(
            ([objectId, value]) => (nodes[objectId].pickable = value)
          );
        } else {
          const objectIds = action.objectIds || Object.keys(nodes);
          objectIds.forEach(objectId => {
            nodes[objectId].pickable = action.pickable;
            if (action.objectIds) {
              setAllChildren(objectId, nodes, 'pickable', action.pickable);
            }
          });
        }
      }
    );

    const finalState = immer.produce(nextState, draft => {
      draft.patches = patches;
      draft.patchesForChapter[draft.activeChapterId].push(...patches);
    });

    context.patchState(finalState);
    context.dispatch(
      new UndoActions.SavePatches([{
        forward: patches,
        inverse: inversePatches,
        patchAction: NodeActions.ApplyPatch
      }])
    );
  }

  @Action(NodeActions.UpdateNodesPaint)
  updateNodesPaint(
    context: StateContext<EngineNodesStateModel>,
    action: NodeActions.UpdateNodesPaint
  ) {
    const state = context.getState();

    const [nextState, patches, inversePatches] = immer.produceWithPatches(
      state,
      draft => {
        const nodes = draft.nodeState[draft.activeChapterId];
        const objectIds = action.objectIds || Object.keys(nodes);

        if (action.patch) {
          Object.entries(action.patch).forEach(([objectId, value]) => {

            const newRecipe = ColorRecipeModel.mergeRecipes(nodes[objectId].paint, value);
            nodes[objectId].paint = newRecipe;

          });
        } else {
          objectIds.forEach(objectId => {
            
            const newRecipe = ColorRecipeModel.mergeRecipes(nodes[objectId].paint, action.recipe);
            nodes[objectId].paint = newRecipe;
            
            if (action.objectIds) {
              setAllChildren(objectId, nodes, 'paint', newRecipe);
            }
          });
        }
      }
    );

    const finalState = immer.produce(nextState, draft => {
      draft.patches = patches;
      draft.patchesForChapter[draft.activeChapterId].push(...patches);
    });

    context.patchState(finalState);

    if (action.record) {
      context.dispatch(
        new UndoActions.SavePatches([{
          forward: patches,
          inverse: inversePatches,
          patchAction: NodeActions.ApplyPatch
        }])
      );
    }
  }



  constructor() {}
}
